import { Common } from 'src/domains/common/Common';
import { ConfigType, DefaultComponentVariantType } from './types';
import { LocalStorageState } from 'src/domains/layouts/state/localStorage/LocalStorageState';
import { AmountPrecision } from 'src_common/common/amount/AmountPrecision';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';

export class ConfigComponents {
    public static get = AutoWeakMap.create((common: Common) => new ConfigComponents(common));
    public constructor(private readonly common: Common) {}

    public get config(): ConfigType {
        if (this.common.isBrowser) {
            const localStorageState = LocalStorageState.get(this.common);

            return {
                ...this.common.features,
                ...localStorageState.debugPanelConfig.getValue(),
            };
        } else {
            return this.common.features;
        }
    }

    public get unifiedComponentConfig(): ConfigType {
        const { componentVariants, unifiedSportEventsTable, geniusHomeInPlayEnabled, geniusSports } = this.config;
        const { currentView } = StarRouter.get(this.common);
        const GENIUS_VARIANT = DefaultComponentVariantType.TERTIARY;
        const unifiedComponentVariantsKeys = [
            'sportsbookVariant',
            'selectionVariant',
            'eventRowVariant',
            'raceCardVariant',
            'scoreVariant',
        ] as const;

        let sportId: string = '';
        let isGlobal = false;

        switch (currentView?.name) {
            case 'sport': {
                sportId = currentView.id;
                break;
            }
            case 'competition': {
                sportId = currentView.slug;
                break;
            }
            case 'event': {
                sportId = this.common.models.getEvent(currentView.id)?.sport ?? '';
                break;
            }
            case 'homepage':
            case 'inplay': {
                isGlobal = geniusHomeInPlayEnabled;
                break;
            }
        }

        const useUnified = unifiedSportEventsTable && (isGlobal || geniusSports.some((sport) => sport === sportId));

        const baseComponentConfig = { ...this.config };

        // If somehow feature flag is true but variant doesn't corresponds new design variant, force it to be DefaultComponentVariantType.TERTIARY
        if (useUnified) {
            return {
                ...baseComponentConfig,
                componentVariants: {
                    ...componentVariants,
                    sportsbookVariant: GENIUS_VARIANT,
                    selectionVariant: GENIUS_VARIANT,
                    eventRowVariant: GENIUS_VARIANT,
                    raceCardVariant: GENIUS_VARIANT,
                    scoreVariant: GENIUS_VARIANT,
                },
            };
        }

        // If somehow feature flag is false, only reset unified-table variants from tertiary to null.
        unifiedComponentVariantsKeys.forEach((variantKey) => {
            if (baseComponentConfig.componentVariants[variantKey] === GENIUS_VARIANT) {
                baseComponentConfig.componentVariants[variantKey] = null;
            }
        });

        return { ...baseComponentConfig, unifiedSportEventsTable: false };
    }

    public get precision(): AmountPrecision {
        return new AmountPrecision(this.config.decimalLength);
    }
}
